import React, { useEffect, useMemo, useState } from "react";
import { ChevronRight, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetBloggerInvitationChildrenQuery } from "../../api";
import type { BloggerInvitationNode } from "../../api/bloggers";
import {
    formatTelegramUsername,
    getInvitationNodeLabel,
} from "./bloggerLabels";

interface InvitationChildrenProps {
    bloggerId: number;
    parentUserId: number;
    enabled: boolean;
    depth: number;
}

const mergeNodes = (
    current: BloggerInvitationNode[],
    incoming: BloggerInvitationNode[]
): BloggerInvitationNode[] => {
    const nodes = new Map(current.map((node) => [node.id, node]));
    let changed = false;

    incoming.forEach((node) => {
        const existing = nodes.get(node.id);
        const isSame =
            existing &&
            Object.keys(node).every(
                (key) =>
                    existing[key as keyof BloggerInvitationNode] ===
                    node[key as keyof BloggerInvitationNode]
            );

        if (!isSame) {
            nodes.set(node.id, node);
            changed = true;
        }
    });

    return changed ? Array.from(nodes.values()) : current;
};

const InvitationNodeRow: React.FC<{
    bloggerId: number;
    node: BloggerInvitationNode;
    depth: number;
}> = ({ bloggerId, node, depth }) => {
    const { t } = useTranslation("common");
    const [expanded, setExpanded] = useState(false);
    const telegram = formatTelegramUsername(node.telegram_username);
    const hasChildren = node.children_count > 0;

    return (
        <li className="relative">
            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                <button
                    type="button"
                    onClick={() => hasChildren && setExpanded((value) => !value)}
                    disabled={!hasChildren}
                    aria-expanded={hasChildren ? expanded : undefined}
                    aria-label={
                        hasChildren
                            ? t(
                                  expanded
                                      ? "bloggers.collapse_invites"
                                      : "bloggers.expand_invites",
                                  { name: getInvitationNodeLabel(node) }
                              )
                            : t("bloggers.no_invited_children")
                    }
                    className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:cursor-default disabled:opacity-30 dark:hover:bg-gray-700"
                >
                    <ChevronRight
                        size={17}
                        aria-hidden="true"
                        className={`transition-transform ${
                            expanded ? "rotate-90" : ""
                        }`}
                    />
                </button>

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium text-gray-900 dark:text-white">
                            {getInvitationNodeLabel(node)}
                        </span>
                        {telegram && (
                            <span className="text-sm text-sky-600 dark:text-sky-400">
                                {telegram}
                            </span>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            #{node.id}
                            {node.secret_cookie
                                ? ` · ${node.secret_cookie}`
                                : ""}
                        </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                node.ever_paid
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                    : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                            }`}
                        >
                            {node.ever_paid
                                ? t("bloggers.paid")
                                : t("bloggers.not_paid")}
                        </span>
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                node.current_active
                                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                    : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                            }`}
                        >
                            {node.current_active
                                ? t("bloggers.active")
                                : t("bloggers.inactive")}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t("bloggers.invited_children_count", {
                                count: node.children_count,
                            })}
                        </span>
                    </div>
                </div>
            </div>

            {hasChildren && expanded && (
                <InvitationChildren
                    bloggerId={bloggerId}
                    parentUserId={node.id}
                    enabled={expanded}
                    depth={depth + 1}
                />
            )}
        </li>
    );
};

const InvitationChildren: React.FC<InvitationChildrenProps> = ({
    bloggerId,
    parentUserId,
    enabled,
    depth,
}) => {
    const { t } = useTranslation("common");
    const [page, setPage] = useState(1);
    const [loadedNodes, setLoadedNodes] = useState<BloggerInvitationNode[]>([]);
    const {
        data,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useGetBloggerInvitationChildrenQuery(
        {
            bloggerId,
            parentUserId,
            page,
            perPage: 50,
        },
        { skip: !enabled, refetchOnMountOrArgChange: true }
    );

    useEffect(() => {
        setPage(1);
        setLoadedNodes([]);
    }, [bloggerId, parentUserId]);

    useEffect(() => {
        if (data?.data) {
            setLoadedNodes((current) => mergeNodes(current, data.data));
        }
    }, [data]);

    const nodes = useMemo(
        () =>
            page === 1 && loadedNodes.length === 0 && data?.data
                ? data.data
                : loadedNodes,
        [data, loadedNodes, page]
    );
    const hasMore = data ? data.meta.current_page < data.meta.last_page : false;

    if (isLoading && nodes.length === 0) {
        return (
            <div
                className={depth > 0 ? "ml-5 border-l-2 pl-5" : ""}
                role="status"
            >
                <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
                    {t("bloggers.loading_invitation_tree")}
                </p>
            </div>
        );
    }

    if (isError && nodes.length === 0) {
        return (
            <div
                className={`py-4 ${depth > 0 ? "ml-5 border-l-2 pl-5" : ""}`}
            >
                <p className="text-sm text-red-600">
                    {t("bloggers.invitation_tree_error")}
                </p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                    <RefreshCw size={14} aria-hidden="true" />
                    {t("bloggers.retry")}
                </button>
            </div>
        );
    }

    if (nodes.length === 0) {
        return (
            <div
                className={`rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400 ${
                    depth > 0 ? "ml-5 mt-2" : ""
                }`}
            >
                {t("bloggers.no_invitation_children")}
            </div>
        );
    }

    return (
        <div
            className={
                depth > 0
                    ? "ml-5 mt-2 border-l-2 border-blue-100 pl-5 dark:border-blue-900/50"
                    : ""
            }
        >
            <ul className="space-y-2">
                {nodes.map((node) => (
                    <InvitationNodeRow
                        key={node.id}
                        bloggerId={bloggerId}
                        node={node}
                        depth={depth}
                    />
                ))}
            </ul>

            {(hasMore || (isError && nodes.length > 0)) && (
                <button
                    type="button"
                    onClick={() =>
                        isError ? refetch() : setPage((value) => value + 1)
                    }
                    disabled={isFetching}
                    className="mt-3 rounded-md border border-blue-200 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950/30"
                >
                    {isFetching
                        ? t("bloggers.loading_more")
                        : isError
                          ? t("bloggers.retry")
                          : t("bloggers.load_more")}
                </button>
            )}
        </div>
    );
};

interface BloggerInvitationTreeProps {
    bloggerId: number;
    rootUserId: number;
}

const BloggerInvitationTree: React.FC<BloggerInvitationTreeProps> = ({
    bloggerId,
    rootUserId,
}) => (
    <InvitationChildren
        key={`${bloggerId}-${rootUserId}`}
        bloggerId={bloggerId}
        parentUserId={rootUserId}
        enabled
        depth={0}
    />
);

export default BloggerInvitationTree;
